import React, { useEffect } from 'react';
import { Box, TextField, Typography, FormControlLabel, Checkbox, Switch, Divider, MenuItem, Grid } from '@mui/material';
import { useFormikContext, FieldArray } from 'formik';

const Step4Ratings = () => {
  const { values, setFieldValue } = useFormikContext();

  const ratingTypeOptions = ['numbers', 'stars'];

  const handleChange = (field) => (e) => {
    setFieldValue(`ratings.${field}`, e.target.value);
  };

  const handleNestedChange = (field1, field2) => (e) => {
    setFieldValue(`ratings.${field1}.${field2}`, e.target.value);
  };

  const handleCheckboxChange = (field) => (e) => {
    setFieldValue(`ratings.${field}`, e.target.checked);
  };

  const handleWeightChange = (category) => (e) => {
    setFieldValue(`ratings.moduleWeighting.weights.${category}`, e.target.value);
  };

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <Typography variant="h6">Rating Type</Typography>
      <Box
        sx={{
          backgroundColor: 'grey.200',
          p: 2,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <TextField
          size="small"
          select
          label="Rating Type"
          value={values.ratings.ratingType}
          onChange={handleChange('ratingType')}
          fullWidth
        >
          {ratingTypeOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              size="small"
              label="Range Min"
              value={values.ratings.range.min}
              onChange={handleNestedChange('range', 'min')}
              fullWidth
              type="number"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              size="small"
              label="Range Max"
              value={values.ratings.range.max}
              onChange={handleNestedChange('range', 'max')}
              fullWidth
              type="number"
            />
          </Grid>
        </Grid>
        <TextField
          size="small"
          label="Decimal Points"
          value={values.ratings.decimalPoints}
          onChange={handleChange('decimalPoints')}
          fullWidth
          type="number"
        />
      </Box>

      <FormControlLabel
        control={<Checkbox checked={values.ratings.includeNA} onChange={handleCheckboxChange('includeNA')} />}
        label="Do you want to include the not applicable (N/A) option in ratings?"
      />

      <TextField
        size="small"
        label="Specify characters"
        value={values.ratings.minimumCommentLength}
        onChange={handleChange('minimumCommentLength')}
        fullWidth
        type="number"
      />

      <Box>
        <Typography variant="h6" gutterBottom>
          Rating Scale
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <FieldArray
          name="ratings.ratingScale"
          render={() => (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}
            >
              {values.ratings.ratingScale.map((scale, index) => (
                <React.Fragment key={index}>
                  <Box
                    sx={{
                      backgroundColor: 'grey.200',
                      p: 2,
                      m: 2,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {/* <Typography>{scale.label}</Typography> */}
                    <TextField
                      size="small"
                      label="Title"
                      value={scale.label}
                      fullWidth
                      onChange={(e) => setFieldValue(`ratings.ratingScale[${index}].label`, e.target.value)}
                    />

                    <TextField
                      size="small"
                      label="Min"
                      type="number"
                      fullWidth
                      value={scale.min}
                      onChange={(e) => setFieldValue(`ratings.ratingScale[${index}].min`, e.target.value)}
                    />

                    <TextField
                      label="Max"
                      size="small"
                      type="number"
                      fullWidth
                      value={scale.max}
                      onChange={(e) => setFieldValue(`ratings.ratingScale[${index}].max`, e.target.value)}
                    />
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
        />
      </Box>

      <Box>
        <Typography variant="h6" gutterBottom>
          Module Weighting
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={values.ratings.moduleWeighting.enabled}
              onChange={(e) => setFieldValue('ratings.moduleWeighting.enabled', e.target.checked)}
            />
          }
          label="Do you want each evaluation method to have a defined relative importance towards overall rating
calculation? (enter weight for each)"
        />
        {values.ratings.moduleWeighting.enabled && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {['Goals', 'KRA', 'Skill', 'Will'].map((item) => (
              <Grid item xs={6} md={3} key={item}>
                <TextField
                  size="small"
                  label={`${item} Weight (%)`}
                  type="number"
                  fullWidth
                  value={values.ratings.moduleWeighting.weights[item] || ''}
                  onChange={handleWeightChange(item)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default Step4Ratings;
